import { useEffect, useRef, useState } from 'react'

import { CounselProMark } from '@/components'
import { ToastAction } from '@/components/ui/Toast'
import { useTitleFlash } from '@/hooks'
import { toast } from '@/hooks/useToast'
import { useHelpActions } from '@/modules/help/store'
import { useSupportEvents } from '../hooks/useSupportEvents'
import { buildMessagePreview, staffFirstName } from '../messagePreviews'
import { playNotificationSound } from '../notificationSound'
import {
  isTicketUnread,
  useSupportStore,
  useSupportUnreadCount,
} from '../store'
import type { SupportMessage } from '../types'

// Headless: keeps the support SSE stream open for the authenticated layout,
// and on a staff reply raises a toast, plays a sound, and flashes the tab until
// the ticket is read.
export const SupportRealtime = () => {
  const { openTickets } = useHelpActions()
  const unreadCount = useSupportUnreadCount()
  const [replyArrived, setReplyArrived] = useState(false)
  const dismissToastByTicket = useRef(new Map<string, () => void>())

  // Unread is remembered across sessions, so it alone would strobe the tab at
  // every login. Only a reply that lands while the app is open earns that.
  useEffect(() => {
    if (unreadCount === 0) setReplyArrived(false)
  }, [unreadCount])

  // The toast stays up until the reply is seen. Reading the thread through any
  // door (the pill, a deep link, the toast itself) takes it down.
  useEffect(
    () =>
      useSupportStore.subscribe((state) => {
        for (const [ticketPublicId, dismiss] of dismissToastByTicket.current) {
          const ticket = state.tickets.find(
            (candidate) => candidate.publicId === ticketPublicId,
          )
          if (ticket && isTicketUnread(ticket, state.lastSeenAt)) continue
          dismiss()
          dismissToastByTicket.current.delete(ticketPublicId)
        }
      }),
    [],
  )

  useTitleFlash(replyArrived ? unreadCount : 0, 'New reply')

  const raiseReplyToast = (ticketPublicId: string, message: SupportMessage) => {
    const subject = useSupportStore
      .getState()
      .tickets.find((ticket) => ticket.publicId === ticketPublicId)?.subject

    // A newer reply on the same ticket replaces its toast instead of stacking.
    dismissToastByTicket.current.get(ticketPublicId)?.()

    const openTicket = () => {
      openTickets(ticketPublicId)
      dismissToastByTicket.current.get(ticketPublicId)?.()
      dismissToastByTicket.current.delete(ticketPublicId)
    }

    const { dismiss } = toast({
      duration: Number.POSITIVE_INFINITY,
      className:
        'cursor-pointer flex-col items-stretch gap-2.5 space-x-0 border-brand/30 shadow-xl transition-colors hover:border-brand/50 hover:bg-brand/5',
      onClick: openTicket,
      description: (
        <span className="flex gap-2.5">
          <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-brand/10 text-brand-deep">
            <CounselProMark className="h-4 w-4" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-xs font-semibold uppercase tracking-wide text-brand-deep">
              New support reply
            </span>
            <span className="mt-0.5 block truncate text-sm font-semibold text-foreground">
              {subject ?? 'Your support ticket'}
            </span>
            <span className="mt-1 line-clamp-2 block text-sm text-ink-subtle">
              {staffFirstName(message.staffName)}:{' '}
              {buildMessagePreview(message.body) ?? 'Sent an attachment'}
            </span>
          </span>
        </span>
      ),
      action: (
        <ToastAction
          altText="Open the support ticket"
          className="cursor-pointer self-end border-transparent bg-brand-deep text-white hover:bg-brand-deep/90"
          onClick={(event) => {
            event.stopPropagation()
            openTicket()
          }}
        >
          Open ticket
        </ToastAction>
      ),
    })
    dismissToastByTicket.current.set(ticketPublicId, dismiss)
  }

  useSupportEvents({
    onStaffReply: (ticketPublicId, { threadVisible, message }) => {
      // Always audible, even while reading the thread. The toast and the
      // flashing tab are only for a reply the user would otherwise miss.
      playNotificationSound()
      if (threadVisible) return

      setReplyArrived(true)
      raiseReplyToast(ticketPublicId, message)
    },
  })

  return null
}
